import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import PageShell from '@/components/feature/PageShell';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import useSeo from '@/hooks/useSeo';
import { articles, categories, regions, stats } from '@/mocks/incidents';

const ALL_CATEGORY = '全部';

export default function Cases() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get('category') ?? ALL_CATEGORY;
  const activeRegion = searchParams.get('region') ?? regions[0];
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim();
    return [...articles]
      .filter((item) => (activeCategory === ALL_CATEGORY ? true : item.category === activeCategory))
      .filter((item) => (activeRegion === regions[0] ? true : item.region === activeRegion))
      .filter((item) => (kw ? item.title.includes(kw) || item.summary.includes(kw) || item.tags.some((t) => t.includes(kw)) : true))
      .sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [activeCategory, activeRegion, keyword]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === ALL_CATEGORY || value === regions[0]) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://zhimeizhaoming.cn/' },
          { '@type': 'ListItem', position: 2, name: '案件通报', item: 'https://zhimeizhaoming.cn/cases' },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: '案件通报与进展汇总',
        description: '按分类与地区汇总缅北相关的案件、抓捕、解救与通报条目。',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'ItemList',
        itemListElement: filtered.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.title,
          url: `https://zhimeizhaoming.cn/cases/${item.id}`,
        })),
      },
    ],
  };

  useSeo({
    title: '案件通报｜缅北黑料事件案件与抓捕解救进展汇总',
    description:
      '缅北黑料案件通报栏目，汇总缅北黑料事件、缅北黑料案件、缅北黑料通报与抓捕解救进展，支持按事件案件、抓捕行动、解救进展、官方通报、警示科普分类筛选查看。',
    keywords: '缅北黑料通报,缅北黑料案件,缅北黑料抓捕,缅北黑料解救,跨境犯罪通报',
    path: '/cases',
    jsonLd,
  });

  return (
    <PageShell>
      <section className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ label: '首页', to: '/' }, { label: '案件通报' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            案件通报与进展汇总
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600">
            按分类与地区整理的追踪条目，覆盖案件事实、抓捕行动、解救进展与官方通报。每条内容均标注整理来源与时间，便于回看与引用。
          </p>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
            {stats.slice(0, 3).map((item) => (
              <div key={item.label} className="flex items-baseline gap-2">
                <span className="font-heading text-xl font-bold text-primary-600">{item.value}</span>
                <span className="text-xs text-foreground-600">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-12">
          <div className="flex flex-col gap-5 rounded-xl border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-xs font-medium text-foreground-500">分类</span>
              <button
                type="button"
                onClick={() => updateParam('category', ALL_CATEGORY)}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  activeCategory === ALL_CATEGORY
                    ? 'bg-primary-600 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => updateParam('category', cat.key)}
                  className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors ${
                    activeCategory === cat.key
                      ? 'bg-primary-600 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {cat.key}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 border-t border-background-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-medium text-foreground-500">地区</span>
                {regions.map((region) => (
                  <button
                    key={region}
                    type="button"
                    onClick={() => updateParam('region', region)}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs transition-colors ${
                      activeRegion === region
                        ? 'bg-secondary-800 text-background-50'
                        : 'text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {region}
                  </button>
                ))}
              </div>

              <div className="relative w-full sm:w-64">
                <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="搜索标题、摘要或标签"
                  className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <p className="text-xs text-foreground-500">
              共 <span className="font-medium text-foreground-900">{filtered.length}</span> 条结果
              {activeCategory !== ALL_CATEGORY && ` · 分类「${activeCategory}」`}
              {activeRegion !== regions[0] && ` · 地区「${activeRegion}」`}
            </p>
            {(activeCategory !== ALL_CATEGORY || activeRegion !== regions[0] || keyword) && (
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  setSearchParams(new URLSearchParams());
                }}
                className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-primary-600"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            )}
          </div>

          <div className="mt-5 flex flex-col gap-6">
            {filtered.length > 0 ? (
              filtered.map((article) => <ArticleCard key={article.id} article={article} variant="list" />)
            ) : (
              <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-background-300 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
                  <i className="ri-file-search-line"></i>
                </span>
                <p className="mt-4 font-heading text-base font-semibold text-foreground-900">没有匹配的条目</p>
                <p className="mt-1 text-sm text-foreground-500">换个关键词，或重置筛选条件再看看。</p>
                <button
                  type="button"
                  onClick={() => {
                    setKeyword('');
                    setSearchParams(new URLSearchParams());
                  }}
                  className="mt-5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
                >
                  重置筛选
                </button>
              </div>
            )}
          </div>

          <div className="mt-12 rounded-xl border border-background-200 bg-background-100 p-5 md:p-6">
            <h2 className="font-heading text-base font-semibold text-foreground-950">如何高效使用本栏目</h2>
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-filter-3-line"></i>
                </span>
                <p className="text-xs leading-relaxed text-foreground-600">先按分类缩小范围，再用地区定位到具体片区。</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-calendar-schedule-line"></i>
                </span>
                <p className="text-xs leading-relaxed text-foreground-600">条目按时间倒序排列，可顺着时间线回看事件演变。</p>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
                  <i className="ri-link"></i>
                </span>
                <p className="text-xs leading-relaxed text-foreground-600">
                  需要引用时，请以条目内标注的来源与时间为准，并核对原始信息。
                </p>
              </div>
            </div>
            <Link
              to="/about"
              className="mt-5 inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 transition-colors hover:text-primary-800"
            >
              了解我们的内容原则
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}